import React, { useMemo } from 'react'
import {
  PageSection as Section,
  Grid,
  Link,
  Body,
  getImgProps,
  Callout,
  Button,
  Message,
  UserChip,
  Icon,
} from '@superhi/design'

import ROUTE from './route'
import { Props } from './data'
import {
  WebsiteWrapper,
  WebsiteError,
  WebsiteFallbackImage,
  Iframe,
  Info,
  Image,
  ImageWrapper,
  PreviousNextWrapper,
  PreviousNextButtons,
  PreviousButton,
  NextButton,
  InfoUser,
  InfoUrl,
} from './styles'

import { API } from 'src/api'
import Aside from 'src/components/Aside'
import Main from 'src/components/Main'
import Page from 'src/components/Page'
import SEO from 'src/components/SEO'
import PageHeader from 'src/components/page-headers/PageHeader'
import { buildPath } from 'src/routing'
import { PageConfig } from 'src/routing/types'

const HallOfFamer: PageConfig<Props> = ({ title, type, url, users, image, previous, next }) => {
  const [error, setError] = React.useState(false)
  const [errorDismissed, setErrorDismissed] = React.useState(false)

  const by = users
    ?.map((user) => user.name || user.username)
    .filter(Boolean)
    .join(', ')

  const imageProps = useMemo(
    () =>
      getImgProps(image.url, {
        small: 90,
        medium: 90,
        large: 90,
        xlarge: 90,
      }),
    [image.url],
  )

  const prettyUrl = url ? (type === API.HallOfFamerType.DESIGN ? 'View design' : url) : undefined

  return (
    <Page>
      <SEO
        title="See our students' work in the SuperHi Hall of Fame"
        description="Our students make amazing things. Here’s a small selection of the design and code projects they've been working on."
      />

      <Main>
        <PageHeader title={`${title}${by ? ` by ${by}` : ''}`} />

        <Section noTopPadding>
          <PreviousNextWrapper>
            <Grid>
              <Grid.Cell span="full">
                {type === API.HallOfFamerType.WEBSITE && url ? (
                  <WebsiteWrapper>
                    <Iframe
                      src={url}
                      ref={(e) => {
                        if (!e?.contentDocument?.URL) {
                          setError(true)
                        }
                      }}
                    />
                    {error && (
                      <WebsiteFallbackImage
                        {...imageProps}
                        fit="cover"
                        position="top center"
                        alt=""
                      />
                    )}
                    {error && !errorDismissed && (
                      <WebsiteError>
                        <Message level="3" color="RED_10">
                          <span>This site isn’t loading – try viewing it directly by clicking</span>{' '}
                          <Link href={url} isExternal>
                            here
                          </Link>
                          <span>.</span>
                        </Message>
                      </WebsiteError>
                    )}
                  </WebsiteWrapper>
                ) : (
                  <ImageWrapper>
                    <Image {...imageProps} blurHash={image.blurhash} alt="" />
                  </ImageWrapper>
                )}
              </Grid.Cell>
            </Grid>

            {(next || previous) && (
              <PreviousNextButtons>
                {next && (
                  <PreviousButton
                    href={buildPath('hall-of-fame-view', {
                      params: { slug: next.slug },
                    })}
                  >
                    <Icon name="arrow-left" color="WHITE" />
                  </PreviousButton>
                )}
                {previous && (
                  <NextButton
                    href={buildPath('hall-of-fame-view', {
                      params: { slug: previous.slug },
                    })}
                  >
                    <Icon name="arrow-right" color="WHITE" />
                  </NextButton>
                )}
              </PreviousNextButtons>
            )}
          </PreviousNextWrapper>

          <Info>
            <Grid rowGap={{ small: 8 }}>
              <Grid.Cell span={{ small: 'full', medium: 4, large: 6 }}>
                <InfoUser>
                  {users?.map((user) => (
                    <UserChip
                      key={user.id}
                      image={{ src: user.avatar?.url || '' }}
                      name={user.name || user.username}
                      level="2"
                      // link={{
                      //   href: `${DASHBOARD_URL}/users/${user.username}`,
                      //   isExternal: true,
                      // }}
                    />
                  )) ||
                    (by && (
                      <Body color="GREY_70" level="3" inline>
                        By {by}
                      </Body>
                    ))}
                </InfoUser>
              </Grid.Cell>
              <Grid.Cell span={{ small: 'full', medium: 4, large: 6 }}>
                <InfoUrl>
                  {prettyUrl && url && (
                    <div>
                      <Body level="3">
                        <Link href={url} isExternal>
                          {prettyUrl}
                        </Link>
                      </Body>
                    </div>
                  )}
                </InfoUrl>
              </Grid.Cell>
            </Grid>
          </Info>
        </Section>

        <Aside>
          <Callout
            backgroundColor="blue"
            title="Want something you made featured in the Student Hall Of Fame?"
          >
            <Button
              level={Callout.ButtonLevel}
              href="https://superhi.typeform.com/to/CqVmy4ot"
              isExternal
            >
              Submit your work
            </Button>
          </Callout>
        </Aside>

        {/* <Section title="More student work">
        <Grid alignItems="stretch" slider={{ small: true }}>
          {more.map((item) => (
            <Grid.Cell key={item.slug} span={{ small: 2, medium: 4, large: 4 }}>
              <StudentWorkCard {...item} />
            </Grid.Cell>
          ))}
        </Grid>
      </Section> */}
      </Main>
    </Page>
  )
}

HallOfFamer.route = ROUTE

export { getStaticPaths, getStaticProps } from './data'
export default HallOfFamer
